import { setExamOverview, setCurrentSection, updateTimeRemaining, markAnswerPending, markAnswerSaved, enqueueAnswer, setExamComplete, setLoadingSection, setLastError, setMissingQuestions } from "../Reducer/examSlice";
import { wsConnecting, wsConnected, wsDisconnected, wsError, setPendingAnswersCount, setReconnectAttempts, setNetworkOnline } from "../Reducer/websocketSlice";

let ws = null;
let heartbeatTimer = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
const HEARTBEAT_INTERVAL_MS = 30000;

let assignmentIdGlobal = null;
let networkWatchersInitialized = false;

function makeWsUrl(assignmentId) {
  const base = process.env.REACT_APP_WS_BASE;
  return `${base}api/v1/websocket/exam/${assignmentId}`;
}

function isBrowserOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

// Detach handlers and close the current socket so its stale callbacks can no
// longer mutate state after we've moved on.
function teardownSocket(code = 4000, reason = "client_teardown") {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  if (ws) {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      ws.close(code, reason);
    } catch (e) {
      /* ignore */
    }
    ws = null;
  }
}

// (Re)connect the socket after `delay` ms, unless it is already open, the exam
// has finished, or the browser is offline (the "online" event handles that case).
function scheduleReconnect(store, delayMs) {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws && ws.readyState === WebSocket.OPEN) return;

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (store.getState().exam.examCompleted) return;
    if (isBrowserOffline()) return;
    if (ws && ws.readyState === WebSocket.OPEN) return;
    initWebsocket(assignmentIdGlobal, store);
  }, delayMs);
}

// Listen for the browser going offline / online so we can show the snackbar and
// reconnect immediately instead of waiting out the backoff.
function initNetworkWatchers(store) {
  if (networkWatchersInitialized || typeof window === "undefined") return;
  networkWatchersInitialized = true;

  window.addEventListener("offline", () => {
    store.dispatch(setNetworkOnline(false));
    store.dispatch(wsDisconnected());
    // Drop the (now dead) socket so "online" starts a clean reconnect.
    teardownSocket(4000, "offline");
  });

  window.addEventListener("online", () => {
    store.dispatch(setNetworkOnline(true));
    if (store.getState().exam.examCompleted) return;
    // Retry right away instead of waiting out the backoff. reconnectAttempts is
    // left as-is so onopen still treats this as a reconnect and resyncs state.
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      scheduleReconnect(store, 0);
    }
  });
}

export function initWebsocket(assignmentId, store) {
  const { dispatch, getState } = store;

  initNetworkWatchers(store);

  // Guard: do not create duplicate WS
  if (
    ws &&
    (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)
  ) {
    return ws;
  }

  assignmentIdGlobal = assignmentId;
  dispatch(wsConnecting());

  // Nothing to connect to while the browser is offline — wait for "online".
  if (isBrowserOffline()) {
    dispatch(setNetworkOnline(false));
    dispatch(wsDisconnected());
    return null;
  }

  const url = makeWsUrl(assignmentId);

  try {
    ws = new WebSocket(url);
  } catch (err) {
    dispatch(wsError("Failed to create WebSocket"));
    return null;
  }

  ws.onopen = () => {
    const isReconnect = reconnectAttempts > 0;
    
    reconnectAttempts = 0;
    dispatch(wsConnected());
    dispatch(setReconnectAttempts(0));

    const queue = getState().exam.answersQueue || [];
    queue.forEach((q) => {
      try { ws.send(JSON.stringify(q)); } catch (err) {}
    });

    // request current section from backend to sync state after reconnect
    if (isReconnect) {
      requestCurrentSection(store);
    }

    // start heartbeat
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    heartbeatTimer = setInterval(() => {
      try {
        const state = getState();
        const heartbeat = {
          type: "heartbeat",
          timestamp: new Date().toISOString(),
          time_remaining: state.exam.timeRemaining,
          current_section: state.exam.currentSection?.section_name ?? null,
          answered_questions_count: Object.keys(state.exam.answeredQuestions || {}).length,
        };
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(heartbeat));
      } catch (err) {
        // ignore
      }
    }, HEARTBEAT_INTERVAL_MS);
  };

  ws.onmessage = (event) => {
    let msg = null;
    try {
      msg = JSON.parse(event.data);
    } catch (e) {
      console.error("Invalid WS JSON", e);
      return;
    }

    handleServerMessage(msg, dispatch, getState);
  };

  ws.onclose = (ev) => {
    dispatch(wsDisconnected());
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    if (isBrowserOffline()) {
      dispatch(setNetworkOnline(false));
    }
    // reconnect with backoff unless it was a clean, intentional close (code 1000)
    if (ev.code !== 1000 && !getState().exam.examCompleted) {
      reconnectAttempts++;
      dispatch(setReconnectAttempts(reconnectAttempts));
      const backoff = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
      // If offline, the "online" event will reconnect; the backoff is a fallback.
      scheduleReconnect(store, backoff);
    }
  };

  ws.onerror = (err) => {
    dispatch(wsError("WebSocket error"));
    console.error("WebSocket error:", err);
  };

  return ws;
}

function handleServerMessage(msg, dispatch, getState) {
  const type = msg.type;
  switch (type) {
    case "exam_overview":
      dispatch(setExamOverview(msg));
      break;
    case "section_data":
      dispatch(setLoadingSection(true));
      dispatch(setCurrentSection(msg));
      if (msg.estimated_time_minutes) {
        dispatch(updateTimeRemaining(msg.estimated_time_minutes * 60));
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      break;
    case "section_transition":
      dispatch(updateTimeRemaining(msg.time_remaining_seconds ?? msg.time_remaining ?? getState().exam.timeRemaining));
      break;
    case "section_incomplete":
      dispatch(setLastError(msg.message));
      dispatch(setMissingQuestions(msg.missing_questions));
      break;
    case "answer_saved":
      dispatch(markAnswerSaved({ assignment_question_id: msg.assignment_question_id }));
      const pendingCount = (getState().exam.answersQueue || []).length;
      dispatch(setPendingAnswersCount(pendingCount));
      break;
    case "answer_error":
      dispatch(setLastError(msg.error || msg.message || "Answer save error"));
      break;
    case "heartbeat_ack":
      dispatch(updateTimeRemaining(msg.time_remaining ?? msg.time_remaining_seconds ?? getState().exam.timeRemaining));
      break;
    case "exam_complete":
      dispatch(setExamComplete());
      break;
    case "exam_completed_already":
      dispatch(setExamComplete());
      break;
    case "audio_uploaded":
    case "audio_url":
    case "audio_upload_success":
      dispatch({ type: "UPLOAD_AUDIO_SUCCESS", payload: msg.data || msg });
      break;
    case "pong":
      break;
    case "error":
      dispatch(setLastError(msg.message || "Server error"));
      break;
    default:
      console.warn("Unhandled WS message type:", type);
  }
}

export function sendAnswerMessage(answerPayload, store) {
  const { dispatch, getState } = store;
  const msg = answerPayload;

  if (msg.assignment_question_id) {
    dispatch(markAnswerPending({
      assignment_question_id: msg.assignment_question_id,
      payload: {
        question_id: msg.answer.question_id,
        answer: msg.answer,
      },
    }));
  }

  if (ws && ws.readyState === WebSocket.OPEN) {
    try {
      ws.send(JSON.stringify(msg));
    } catch (err) {
      dispatch(enqueueAnswer(msg));
    }
  } else {
    dispatch(enqueueAnswer(msg));
  }

  const pendingCount = (getState().exam.answersQueue || []).length;
  store.dispatch(setPendingAnswersCount(pendingCount));
}

export function flushQueuedAnswers(store) {
  const { getState } = store;
  const queue = getState().exam.answersQueue || [];
  if (!queue.length) return;
  queue.forEach((q) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(q));
      } catch (err) {
        // leave in queue
      }
    }
  });
}

export function requestNextSection(store) {
  const { getState } = store;
  const currentSection = getState().exam.currentSection;
  if (!currentSection) return;
  const message = {
    type: "request_next_section",
    current_section: currentSection.section_name,
  };
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(message));
  else {
    store.dispatch(setLastError("Cannot request next section: offline"));
  }
}

export function requestCurrentSection(store) {
  const message = { type: "request_current_section" };
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(message));
}

export function closeWebsocket() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  teardownSocket(1000, "client_close");
}